import { useIsMounted } from 'common/modules/ReactShared';
import React, { MutableRefObject, useEffect, useRef, useState } from 'react';
import useAnimateScenarios from './useAnimateScenarios';
import useAudioRecording from './useAudioRecording';
import useBreakPoint, {
  useDesktopBreakPoint,
  useMobileBreakPoint
} from './useBreakPoint';
import useBreakpointNumber from './useBreakpointNumber';
import useExitPrompt from './useExitPrompt';
import useAccessibleListNavigation from './useKeyboardListNavigation';
import useLessonPause from './useLessonPause';
import { useMediaQuery } from './useMediaQuery';
import useMobileVhHack from './useMobileVhHack';
import useOnScreen from './useOnScreen';
import useOutsideClick from './useOutsideClick';
import usePageVisibility from './usePageVisibility';
import usePromptContext from './usePromptContext';

export {
  useAccessibleListNavigation,
  useAnimateScenarios,
  useAudioRecording,
  useBreakPoint,
  useBreakpointNumber,
  useDesktopBreakPoint,
  useExitPrompt,
  useIsMounted,
  useLessonPause,
  useMediaQuery,
  useMobileBreakPoint,
  useMobileVhHack,
  useOnScreen,
  useOutsideClick,
  usePageVisibility,
  usePromptContext
};

export function useCombinedRefs<T>(
  ...refs: MutableRefObject<T | null>[]
): React.RefObject<T> {
  const targetRef = useRef<T>(null);

  useEffect(() => {
    refs.forEach((ref) => {
      if (!ref) return;

      ref.current = targetRef.current;
    });
  }, [refs]);

  return targetRef;
}

export function useMatchMedia(mediaQueryString = ''): MediaQueryList {
  return window.matchMedia(mediaQueryString);
}

const onMountClassName = 'mountedComponentAnimation';
function useAddClassNameOnMount(className = onMountClassName): string {
  const [finalClassName, setFinalClassName] = useState('');

  useEffect(() => {
    setFinalClassName(className);
  }, [className]);

  return finalClassName;
}
useAddClassNameOnMount.className = onMountClassName;

export { useAddClassNameOnMount };

export function useWrongAnimation(
  duration = 800,
  wrongClassName = 'wrongAnimation'
): [React.MutableRefObject<HTMLDivElement | null>, () => void] {
  const wrongTimer = useRef<number | undefined>(undefined);
  const wrongBlockRef = useRef<HTMLDivElement>(null);

  function markWrong() {
    wrongBlockRef.current?.classList.remove(wrongClassName);
    wrongBlockRef.current?.getBoundingClientRect();
    wrongBlockRef.current?.classList.add(wrongClassName);
    clearTimeout(wrongTimer.current);
    wrongTimer.current = window.setTimeout(() => {
      wrongBlockRef.current?.classList.remove(wrongClassName);
    }, duration);
  }

  return [wrongBlockRef, markWrong];
}
